<template>
  <div class="profile-v2">
    <ProfileHero
      v-model="activeTab"
      :display-name="displayName"
      :location="heroLocation"
      :joined-at="joinedAt"
      :company-logo-id="profile.companyLogoId"
      :cover-image-id="profile.coverImageId"
      :is-read-only="isReadOnlyProfile"
      :target-tenant-id="viewingTenantId"
      :counts="heroCounts"
      :review-average-score="reviewAverageScore"
      :review-total-count="reviewTotalCount"
      @profile-data-changed="onProfileDataChanged"
      @cover-changed="saveCoverImage"
    >
      <template #actions>
        <button type="button" class="pv-btn pv-btn--ghost" @click="shareProfile">
          <i class="ph-duotone ph-share-network"></i>{{ $t('profile.hero.share') }}
        </button>
        <button
          v-if="!isReadOnlyProfile"
          type="button"
          class="pv-btn pv-btn--accent"
          @click="activeTab = 'account'"
        >
          <i class="ph-duotone ph-pencil-simple"></i>{{ $t('profile.hero.edit') }}
        </button>
      </template>
    </ProfileHero>

    <div class="pv-body">
      <div class="pv-main">
        <!-- Overview -->
        <template v-if="activeTab === 'overview'">
          <!--
            Four figures that answer "is this profile working for me": what is waiting,
            how wide the coverage is, and whether the paperwork is in place.
          -->
          <div class="vp-tiles">
            <div v-for="kpi in kpis" :key="kpi.key" class="vp-tile pv-kpi" :style="{ borderTopColor: kpi.edge }">
              <div class="vp-tile-top">
                <div class="vp-tile-label vp-truncate"><i class="ph-duotone" :class="kpi.icon"></i>{{ kpi.label }}</div>
              </div>
              <div class="vp-tile-value">{{ kpi.value }}</div>
              <div class="vp-tile-label" :class="{ 'vp-text-danger': kpi.alert }">{{ kpi.note }}</div>
            </div>
          </div>

          <ProfileAboutCard
            :about="profile.about"
            :is-loading="isLoading"
            :is-read-only="isReadOnlyProfile"
            @updated="loadCompanyProfile"
          />

          <!-- Supply coverage: where they deliver. -->
          <ProfilePortCard
            :collapsible="false"
            :can-assign-port-to-tenant="!isReadOnlyProfile && portPermissions.canAssignPortToTenant()"
            :can-edit-ports="!isReadOnlyProfile && portPermissions.canEditPorts()"
            :can-delete-ports="!isReadOnlyProfile && portPermissions.canDeletePorts()"
            :target-tenant-id="viewingTenantId"
            @ports-updated="onProfileDataChanged"
          />
        </template>

        <!-- Product categories -->
        <ProfileCategoryTagsCard
          v-else-if="activeTab === 'categories'"
          :collapsible="false"
          :can-manage-category-tags="!isReadOnlyProfile && canManageCategoryTags"
          :target-tenant-id="viewingTenantId"
          @categories-updated="onProfileDataChanged"
        />

        <!-- Certifications -->
        <ProfileCertificationsCard
          v-else-if="activeTab === 'certificates'"
          :collapsible="false"
          :is-read-only="isReadOnlyProfile"
          :target-tenant-id="viewingTenantId"
          @certificates-updated="onProfileDataChanged"
        />

        <!-- Account -->
        <ProfileAccountForm
          v-else-if="activeTab === 'account'"
          :profile="profile"
          :is-saving="isSaving"
          :is-loading="isLoading"
          :is-read-only="isReadOnlyProfile"
          @submit="updateProfile"
        />

        <!--
          Reviews stay mounted behind v-show: the card is what loads the review summary
          the hero's rating and the tab count read, so it must load on page open, not on
          first visit to the tab.
        -->
        <ProfileReviewsCard
          v-show="activeTab === 'reviews'"
          :target-tenant-id="viewingTenantId"
          :supplier-display-name="reviewsSupplierDisplayName"
          @summary-loaded="handleReviewSummaryLoaded"
        />
      </div>

      <aside class="pv-rail">
        <!--
          Own profile only: the score is about the signed-in tenant. It also retires itself
          once the profile is complete - a checklist with every row ticked is a permanent
          nag with nothing left to ask for. The rail column is a fixed 330px track, so the
          page does not reflow when it goes; the read-only profile already renders this
          rail with the company card alone.
        -->
        <ProfileStrengthCard
          v-if="!isReadOnlyProfile && !completion?.isComplete"
          :completion="completion"
        />

        <ProfileCompanyCard
          :profile="profile"
          :is-loading="isLoading"
          :is-read-only="isReadOnlyProfile"
          :target-tenant-id="viewingTenantId"
          @update:address="onProfileDataChanged"
        />

        <div v-if="!isReadOnlyProfile" class="pv-help">
          <div class="pv-help-head">
            <i class="ph-duotone ph-lifebuoy"></i>
            <h6>{{ $t('profile.help.title') }}</h6>
          </div>
          <p>{{ $t('profile.help.description') }}</p>
          <a class="pv-btn pv-btn--ghost pv-help-action" :href="`mailto:${SUPPORT_EMAIL}`">
            {{ $t('profile.help.contact') }}
          </a>
        </div>
      </aside>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import tenantInfoService from '@/services/tenantInfoService';
import { useMaterialCategoriesPermissions } from '@/composables/useMaterialCategoriesPermissions';
import { usePortPermissions } from '@/composables/usePortPermissions';
import { useProfileCompletion } from '@/composables/useProfileCompletion';

import ProfileHero from '@/components/profile/ProfileHero.vue';
import ProfileStrengthCard from '@/components/profile/ProfileStrengthCard.vue';
import ProfileAboutCard from '@/components/profile/ProfileAboutCard.vue';
import ProfilePortCard from '@/components/profile/ProfilePortCard.vue';
import ProfileCategoryTagsCard from '@/components/profile/ProfileCategoryTagsCard.vue';
import ProfileCertificationsCard from '@/components/profile/ProfileCertificationsCard.vue';
import ProfileCompanyCard from '@/components/profile/ProfileCompanyCard.vue';
import ProfileReviewsCard from '@/components/profile/ProfileReviewsCard.vue';
import ProfileAccountForm from '@/components/profile/ProfileAccountForm.vue';

const SUPPORT_EMAIL = 'support@vesproc.com';

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const portPermissions = usePortPermissions();
const categoryPermissions = useMaterialCategoriesPermissions();
const { completion, load: loadProfileCompletion, refresh: refreshProfileCompletion } = useProfileCompletion();

const activeTab = ref('overview');
/** ?focus= targets that are a tab of their own rather than a card on the overview. */
const FOCUS_TABS = ['account', 'categories', 'reviews', 'certificates'];
const isLoading = ref(true);
const isSaving = ref(false);
const tenantDisplayName = ref(null);
const reviewAverageScore = ref(null);
const reviewTotalCount = ref(null);

const profile = ref({
  displayName: '',
  email: '',
  phone: '',
  about: '',
  companyLogoId: '',
  coverImageId: ''
});

const currentUser = computed(() => store.getters['auth/currentUser']);

/*
  A shared link carries ?tenant=; opening a supplier from the finder still uses
  sessionStorage. Reading both means a pasted link works in a fresh tab, where the
  session value does not exist.
*/
/*
  Filled in by resolveSlugTenant() from the slug in the URL. A shared link carries only
  the readable slug, so the tenant behind it is not known until the server says so.
*/
const resolvedSlugTenantId = ref(null);

const requestedTenantId = computed(() => {
  // Links shared before slugs existed still carry ?tenant=; keep honouring them.
  if (route.query.tenant) return String(route.query.tenant);
  if (route.name !== 'ProfileByMode') return null;
  return resolvedSlugTenantId.value || sessionStorage.getItem('profileTargetTenantId') || null;
});

/*
  sessionStorage only ever worked for a link opened in the tab that set it - paste the
  same link into another browser and it resolved to nothing, so the recipient landed on
  their own profile. Asking the server to resolve the slug is what makes the link
  portable; the stored id stays as a fallback for the in-app navigation that sets it.
*/
const resolveSlugTenant = async () => {
  resolvedSlugTenantId.value = null;
  if (route.name !== 'ProfileByMode' || route.query.tenant) return;

  const match = await tenantInfoService.getProfileBySlug(route.params.profileMode);
  if (match?.tenantId) resolvedSlugTenantId.value = match.tenantId;
};

const viewingTenantId = computed(() => requestedTenantId.value || currentUser.value?.tenantId || null);

const isReadOnlyProfile = computed(() => {
  if (!requestedTenantId.value || !currentUser.value?.tenantId) return false;
  return String(requestedTenantId.value).toUpperCase() !== String(currentUser.value.tenantId).toUpperCase();
});

const canManageCategoryTags = computed(
  () => categoryPermissions.canEditMaterialCategories() || categoryPermissions.canCreateMaterialCategories()
);

const entityCounts = computed(() => store.getters.getEntityCounts || {});

const heroCounts = computed(() => ({
  ports: entityCounts.value.portsCount || 0,
  categories: entityCounts.value.materialCategoriesCount || 0,
  certificates: entityCounts.value.certificatesCount || 0
}));

const displayName = computed(() => {
  if (tenantDisplayName.value) return tenantDisplayName.value;

  if (currentUser.value?.isImpersonating) {
    if (currentUser.value.isImpersonatingTenant) {
      return currentUser.value.newTenantName || currentUser.value.impersonatedTenant?.name || '';
    }
    if (currentUser.value.impersonatedUser) {
      return currentUser.value.impersonatedUser.email;
    }
  }

  if (profile.value.displayName) return profile.value.displayName;

  if (currentUser.value?.firstName && currentUser.value?.lastName) {
    return `${currentUser.value.firstName} ${currentUser.value.lastName}`;
  }

  return currentUser.value?.email || '';
});

const heroLocation = ref('');
const joinedAt = ref(null);

const reviewsSupplierDisplayName = computed(
  () => tenantDisplayName.value || profile.value.displayName || currentUser.value?.email || ''
);

/*
  The KPI row reads off data the page already has, so it costs no extra request. The
  certificate tile turns red at zero because that is the one gap that keeps a supplier
  out of buyers' filtered results.
*/
const kpis = computed(() => [
  {
    key: 'ports',
    label: t('profile.counts.ports'),
    value: heroCounts.value.ports,
    note: t('profile.kpis.portsNote'),
    icon: 'ph-anchor',
    edge: 'var(--vp-accent)'
  },
  {
    key: 'categories',
    label: t('profile.counts.categories'),
    value: heroCounts.value.categories,
    note: t('profile.kpis.categoriesNote'),
    icon: 'ph-list-tree',
    edge: 'var(--vp-info)'
  },
  {
    key: 'certificates',
    label: t('profile.counts.certificates'),
    value: heroCounts.value.certificates,
    note: heroCounts.value.certificates
      ? t('profile.kpis.certificatesNote')
      : t('profile.kpis.certificatesMissing'),
    icon: 'ph-certificate',
    edge: heroCounts.value.certificates ? 'var(--vp-primary)' : 'var(--vp-coral)',
    alert: !heroCounts.value.certificates
  },
  {
    key: 'strength',
    label: t('profileCompletion.title'),
    value: `${completion.value?.completionPercentage ?? 0}%`,
    note: completion.value?.isComplete
      ? t('profileCompletion.completeNote')
      : t('profile.kpis.strengthNote'),
    icon: 'ph-chart-line-up',
    edge: 'var(--vp-success)'
  }
  /*
    Profile strength is a private diagnostic about your own profile - what you still have
    to fill in. It is nobody's business when viewing another company, so the tile drops
    out there. The row is an auto-fit grid, so the remaining three stretch to fill it
    rather than leaving a hole.
  */
].filter((kpi) => kpi.key !== 'strength' || !isReadOnlyProfile.value));

const handleReviewSummaryLoaded = (summary) => {
  if (!summary || typeof summary !== 'object') {
    tenantDisplayName.value = null;
    reviewAverageScore.value = null;
    reviewTotalCount.value = null;
    return;
  }

  const root =
    summary.data && typeof summary.data === 'object' && !Array.isArray(summary.data) ? summary.data : summary;

  tenantDisplayName.value = root.tenantName || root.supplierName || root.companyName || null;

  const average = root.averageRating ?? root.averageScore ?? root.average ?? null;
  const total = root.totalReviewCount ?? root.totalReviews ?? root.reviewCount ?? null;

  reviewAverageScore.value = average != null && Number.isFinite(Number(average)) ? Number(average) : null;
  reviewTotalCount.value = total != null && Number.isFinite(Number(total)) ? Number(total) : null;
};

const loadEntityCounts = async () => {
  try {
    const response = await tenantInfoService.getEntityCounts(viewingTenantId.value || null);
    if (response) store.commit('updateEntityCounts', response);
  } catch (error) {
    console.error('Error loading entity counts:', error);
  }
};

/**
 * Anything that changes profile data refreshes both the counts the hero and KPIs read
 * and the completion score. Routing every card through one handler means a new card
 * cannot forget half the job.
 */
const onProfileDataChanged = () => {
  loadEntityCounts();
  if (!isReadOnlyProfile.value) refreshProfileCompletion();
};

const loadCompanyProfile = async () => {
  try {
    const extraProperties = await tenantInfoService.getExtraProperties(viewingTenantId.value);

    Object.assign(profile.value, {
      displayName: extraProperties.displayName || '',
      email: extraProperties.email || '',
      phone: extraProperties.phone || '',
      about: extraProperties.about || '',
      companyLogoId: extraProperties.companyLogoId || '',
      coverImageId: extraProperties.coverImageId || ''
    });

    /*
      The address is a third-party geo blob written when the tenant last saved its
      location, so every step is defensive: a missing or half-written entry costs this
      one line in the header rather than the whole page.
    */
    const geo = extraProperties.geoLocation || extraProperties.GeoLocation || null;
    heroLocation.value =
      geo?.address?.country || geo?.Address?.country || extraProperties.address || '';
    joinedAt.value = extraProperties.creationTime || null;
  } catch (error) {
    console.error('Error loading company profile:', error);
    store.dispatch('notify', { message: t('profile.errors.loadFailed'), type: 'error' });
  } finally {
    isLoading.value = false;
  }
};

const updateProfile = async (formData) => {
  if (isReadOnlyProfile.value) {
    store.dispatch('notify', { message: t('profile.errors.readOnly'), type: 'warning' });
    return;
  }

  isSaving.value = true;
  try {
    /*
      saveExtraProperties removes every property it is not sent, so the current values
      go with the form's. Sending only the form's four fields wiped the logo and the
      cover on every account save.
    */
    const updated = await tenantInfoService.saveExtraProperties({ ...profile.value, ...formData });

    profile.value = {
      displayName: updated.displayName || '',
      email: updated.email || '',
      phone: updated.phone || '',
      about: updated.about || '',
      companyLogoId: updated.companyLogoId || '',
      coverImageId: updated.coverImageId || ''
    };

    store.dispatch('notify', { message: t('profile.success.updated'), type: 'success' });

    // Recompute the score so the rail reflects the save immediately; without this the
    // figure only moved on a page refresh.
    refreshProfileCompletion();
  } catch (error) {
    console.error('Error updating company profile:', error);
    store.dispatch('notify', { message: t('profile.errors.updateFailed'), type: 'error' });
  } finally {
    isSaving.value = false;
  }
};

/** Persists the cover id the hero just uploaded (or '' when it is cleared). */
const saveCoverImage = async (fileId) => {
  const previous = profile.value.coverImageId;
  profile.value.coverImageId = fileId;

  try {
    await tenantInfoService.saveExtraProperties({ ...profile.value, coverImageId: fileId });
    store.dispatch('notify', {
      message: fileId ? t('profile.cover.updated') : t('profile.cover.removed'),
      type: 'success'
    });
  } catch (error) {
    console.error('Error saving cover image:', error);
    profile.value.coverImageId = previous;
    store.dispatch('notify', { message: t('profile.cover.uploadError'), type: 'error' });
  }
};

/** Strips a company name down to the slug the ProfileByMode route expects. */
const toProfileMode = (name) =>
  String(name || '').replace(/[^A-Za-z0-9]/g, '');

/*
  The slug is built from the tenant's saved DisplayName, because that is the one value
  the server slugifies when it resolves the link back. Deliberately NOT displayName
  below it, which falls through to the viewer's own first/last name and then to an email
  address - a slug built from those would differ per viewer and could put someone's
  email in a shared URL.
*/
const shareSlug = computed(() =>
  toProfileMode(profile.value.displayName || tenantDisplayName.value || '')
);

/*
  window.location.href was the first bug: on /profile that is the *viewer's* own profile
  route, so the recipient opened their own page. The tenant GUID that replaced it worked
  but handed an internal identifier to everyone the link reached, so the path now carries
  a readable slug the server resolves instead.
*/
const shareUrl = computed(() => {
  const tenantId = viewingTenantId.value;
  if (!tenantId) return window.location.href;

  // No saved display name means there is nothing resolvable to put in the path. Fall
  // back to the explicit id rather than hand out a link that lands on the wrong profile.
  if (!shareSlug.value) {
    return `${window.location.origin}/profile/Profile?tenant=${encodeURIComponent(tenantId)}`;
  }

  return `${window.location.origin}/profile/${shareSlug.value}`;
});

/** Clipboard API needs a secure context and permission; this always has a path. */
const copyToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to the textarea fallback below.
    }
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();

  let copied = false;
  try {
    copied = document.execCommand('copy');
  } catch {
    copied = false;
  }

  document.body.removeChild(textarea);
  return copied;
};

/*
  Copy only. The Web Share API was tried first and, on desktop, opened the OS share
  sheet — a mail-client picker — then returned without the link ever reaching the
  clipboard. Sharing here means one thing: put the link where it can be pasted.
*/
const shareProfile = async () => {
  const copied = await copyToClipboard(shareUrl.value);
  store.dispatch('notify', {
    message: copied ? t('profile.hero.shareCopied') : t('profile.hero.shareFailed'),
    type: copied ? 'success' : 'error'
  });
};

/**
 * Opens the tab and card named by ?focus=, so a "Go fill" link from the completion
 * checklist lands on the actual field instead of the page's default tab.
 */
const applyFocusTarget = () => {
  const focus = route.query.focus;
  if (!focus) return;

  // Cards with a tab of their own open that tab; everything else lives on the overview.
  activeTab.value = FOCUS_TABS.includes(focus) ? focus : 'overview';

  // 'account' is a tab and 'logo' is the hero tile; neither is a collapsible card, so
  // only the real cards get the expand events.
  if (focus !== 'account' && focus !== 'logo') {
    window.dispatchEvent(new CustomEvent('profile-left-card-open', { detail: { card: focus } }));
    window.dispatchEvent(new CustomEvent('profile-left-card-focus', { detail: { card: focus } }));
  }

  highlightFocusTarget(focus);
};

/**
 * Briefly ring the card the user was sent to. Arriving on a long page with a tab
 * already switched gives no clue which part was meant; a short pulse answers that.
 */
const highlightFocusTarget = (focus) => {
  // Let the tab switch and the card expansion paint first, or the element is either
  // hidden or still collapsed when we measure it.
  setTimeout(() => {
    const el = document.querySelector(`[data-profile-card="${focus}"]`);
    if (!el) return;

    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.remove('profile-focus-pulse');
    // Reading offsetWidth restarts the animation when the same card is targeted twice.
    void el.offsetWidth;
    el.classList.add('profile-focus-pulse');

    setTimeout(() => el.classList.remove('profile-focus-pulse'), 2000);
  }, 250);
};

onMounted(async () => {
  if (!currentUser.value) {
    router.push('/login');
    return;
  }

  // Before anything loads: the slug decides whose profile this is, and every loader
  // below reads viewingTenantId.
  await resolveSlugTenant();

  await loadCompanyProfile();
  loadEntityCounts();

  if (!isReadOnlyProfile.value) loadProfileCompletion();

  applyFocusTarget();
});

watch(() => route.query, (query) => {
  if (query.focus) applyFocusTarget();
});
</script>

<style scoped>
.pv-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 330px);
  gap: 20px;
  align-items: start;
}

.pv-main {
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
}

.pv-rail {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
  position: sticky;
  top: 78px;
}

@media (max-width: 1100px) {
  .pv-body {
    grid-template-columns: minmax(0, 1fr);
  }

  .pv-rail {
    position: static;
  }
}

/* The coloured top edge is what distinguishes the four figures at a glance. */
.pv-kpi {
  border-top: 3px solid var(--vp-gray-300);
}

.pv-kpi .vp-tile-label i {
  margin-right: 6px;
}

/* Hero buttons sit on navy, so they cannot use the light vp-btn palette. */
.pv-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 0.84rem;
  font-weight: 600;
  border-radius: var(--vp-radius-btn);
  padding: 9px 16px;
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;
}

.pv-btn--ghost {
  border: 1px solid rgba(255, 255, 255, 0.28);
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

.pv-btn--ghost:hover {
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  text-decoration: none;
}

.pv-btn--accent {
  border: 0;
  background: var(--vp-accent);
  color: #fff;
}

.pv-btn--accent:hover {
  background: var(--vp-info);
}

.pv-help {
  background: var(--vp-primary);
  border-radius: var(--vp-radius);
  padding: 17px 18px;
  color: #fff;
}

.pv-help-head {
  display: flex;
  align-items: center;
  gap: 9px;
}

.pv-help-head i {
  font-size: 1.1rem;
  color: var(--vp-accent);
}

.pv-help-head h6 {
  margin: 0;
  font-size: 0.92rem;
  font-weight: 700;
}

.pv-help p {
  margin: 7px 0 0;
  font-size: 0.8rem;
  color: #b7c9e2;
}

.pv-help-action {
  margin-top: 12px;
  width: 100%;
  justify-content: center;
}
</style>
